import React, { Dispatch, FC, ReactNode, SetStateAction, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';

import { useModalOpen } from 'src/contexts/ModalOpenContext';

import { useDispatch } from 'src/store/useDispatch';

import { Button } from '../Button/Button';
import { Loader } from '../Loader';
import KycContent from './KycContent';
import UserContent from './UserContent';
import BankAccountContent from './BankAccount';
import WaitingUserContent from './WaitingUser';
import PresaleTransaction from './PresaleTransaction';

import {
  StyledButtonsWrap,
  StyledModalBody,
  StyledModalContainer,
  StyledModalDismiss,
  StyledModalFooter,
  StyledModalHeader,
  StyledModalOverlay,
} from './styled';
import request from 'src/request';
import { AUTH_URL } from 'src/configs';
import { fetchEntities } from 'src/store/slice/entities';
import { PermissionGuard } from 'src/components/PermissionGuard';
import { PERMISSIONS } from 'src/utils/permissions';

export interface IModal {
  data?: any;
  isOpen: boolean;
  loading?: boolean;
  setNotification?: Dispatch<SetStateAction<{ message: string; isError: boolean }>>;
  onClose: () => void;
  /** When set, renders as the main body (table details, presale dialogs, etc.). Legacy table row modals omit this. */
  children?: ReactNode;
  /** Pinned below scrollable body when using children (e.g. form actions). */
  footer?: ReactNode;
  /** Wider body for tabular content (deliveries, logs). */
  wide?: boolean;
}

export const Modal: FC<IModal> = ({ setNotification, data, isOpen, loading, onClose, children, footer, wide }) => {
  const [kycStatus, setKycStatus] = useState('');
  const [feedback, setFeedback] = useState('');
  const [submitLoading, setSubmitLoading] = useState(false);

  const dispatch = useDispatch();
  const { registerModalOpen } = useModalOpen();

  const hasChildren = children != null && children !== false;

  useEffect(() => {
    if (!isOpen) return;
    return registerModalOpen();
  }, [isOpen, registerModalOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  const handleOverlayClick = (event: React.MouseEvent) => {
    if (event.target === event.currentTarget) {
      onClose();
    }
  };

  const handlePanelClick = (event: React.MouseEvent) => {
    event.stopPropagation();
  };

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose]);

  const { pathname } = useLocation();
  const isKyc = pathname === '/admin/kyc';
  const isUsers = pathname === '/admin/users';
  const isBankAccount = pathname === '/admin/bank-account';
  const isWaitingUsersList = pathname === '/admin/waiting-list-users';
  const isPresaleTrx = pathname === '/admin/presale-transactions';

  const legacyContent = useMemo(
    () => (
      <>
        {isKyc && <KycContent data={data} setKycStatus={setKycStatus} setFeedback={setFeedback} />}
        {isUsers && <UserContent data={data} />}
        {isBankAccount && <BankAccountContent data={data} />}
        {isWaitingUsersList && <WaitingUserContent data={data} />}
        {isPresaleTrx && setNotification && (
          <PresaleTransaction data={data} setNotification={setNotification} onClose={onClose} />
        )}
      </>
    ),
    [data, isBankAccount, isKyc, isPresaleTrx, isUsers, isWaitingUsersList, onClose, setNotification],
  );

  const sendKyc = () => {
    setSubmitLoading(true);
    request
      .patch(`${AUTH_URL}/v1/admin/kyc/${data.kyc._id}`, {
        status: kycStatus,
        response_message: feedback,
      })
      .then(() => {
        setNotification?.({ message: 'Status has been updated successfully.', isError: false });
        onClose();
        dispatch(
          fetchEntities({
            type: 'kyc',
            page: 1,
            fields: ['name', 'user_id', '_id', 'status', 'country_code', 'address'],
          }),
        );
      })
      .catch((e) =>
        setNotification?.({ message: e.response?.data?.error ?? 'Unprocessible entity. Try again.', isError: true }),
      )
      .finally(() => setSubmitLoading(false));
  };

  if (!isOpen) {
    return null;
  }

  const modalTree = (
    <StyledModalOverlay isOpen={isOpen} onClick={handleOverlayClick} role="presentation">
      <StyledModalContainer
        isOpen={isOpen}
        loading={loading}
        $hasChildren={hasChildren}
        $wide={wide}
        onClick={handlePanelClick}
        role="dialog"
        aria-modal="true"
      >
        {!loading && (
          <StyledModalHeader>
            <StyledModalDismiss type="button" onClick={onClose} aria-label="Close dialog">
              ×
            </StyledModalDismiss>
          </StyledModalHeader>
        )}
        <StyledModalBody $centered={!!loading}>
          {loading ? (
            <Loader />
          ) : hasChildren ? (
            children
          ) : (
            legacyContent
          )}
        </StyledModalBody>
        {!loading && hasChildren && footer != null && <StyledModalFooter>{footer}</StyledModalFooter>}
        {!loading && !hasChildren && (
          <StyledModalFooter>
            <StyledButtonsWrap>
              <Button text="Cancel" className="outline" onClick={onClose} />
              {isKyc || isWaitingUsersList ? (
                data?.kyc?.status === 'PENDING' && (
                  <PermissionGuard permission={PERMISSIONS.KYC_REVIEW}>
                    <Button
                      text="OK"
                      className="black"
                      onClick={isKyc ? sendKyc : onClose}
                      disabled={submitLoading}
                      isLoading={submitLoading}
                    />
                  </PermissionGuard>
                )
              ) : (
                <Button
                  text="OK"
                  className="black"
                  onClick={isKyc ? sendKyc : onClose}
                  disabled={submitLoading}
                  isLoading={submitLoading}
                />
              )}
            </StyledButtonsWrap>
          </StyledModalFooter>
        )}
      </StyledModalContainer>
    </StyledModalOverlay>
  );

  return createPortal(modalTree, document.body);
};
